<script setup lang="ts">
  import { testIds } from '@gat-ship/types'
  import { IconRestore, IconDeactivate, IconEdit } from '@gat-ship/icons'
  import {
    DataList,
    DataListItem,
    DataListItemAction,
    DataListItemDescription,
    DataListItemTitle,
    DropdownMenu,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuContent
  } from '@gat-ship/vue-components'
  import { useUser } from '@gat-ship/user'
  import { useCompanyList } from '../../composables/useCompanyList'
  import { useCompanyModals } from '../providers/company-modals'

  // Router-agnostic list body shared by the routed CompaniesView (under its
  // PageLayout chrome) and the AdministrationModal companies panel. The host
  // owns the tab state, the surrounding header, and the Add button — this
  // component renders the rows for the active tab (filtered via useCompanyList)
  // with their Activate / Deactivate / Edit dropdown actions.
  const props = defineProps<{
    /** Filter mode: `active` | `inactive` | anything else = show all. */
    activeTab: string
  }>()

  const user = useUser()
  const modals = useCompanyModals()!

  const { companiesFiltered, activeCompanyCount } = useCompanyList(
    () => props.activeTab
  )
</script>

<template>
  <DataList>
    <DataListItem
      v-for="company in companiesFiltered"
      :key="company.code"
      :data-testid="testIds.company.list.row(company.code)"
      content-class="flex-row items-center"
    >
      <div class="flex w-full min-w-0 flex-col">
        <DataListItemTitle>
          {{ company.name }}
        </DataListItemTitle>
        <DataListItemDescription v-if="company.description">
          {{ company.description }}
        </DataListItemDescription>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <DataListItemAction
            :test-id="testIds.company.list.actionDropdown(company.code)"
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            v-if="user.isTenantAdmin.value && company?.canReactivate"
            :data-testid="testIds.company.actions.activate"
            @click="modals.showReactivateCompany({ company })"
          >
            <IconRestore class="size-4" />
            Activate Company
          </DropdownMenuItem>
          <DropdownMenuItem
            v-if="
              user.isTenantAdmin.value &&
              company?.canDeactivate &&
              activeCompanyCount > 1
            "
            :data-testid="testIds.company.actions.deactivate"
            @click="modals.showDeactivateCompany({ company })"
          >
            <IconDeactivate class="size-4" />
            Deactivate Company
          </DropdownMenuItem>
          <DropdownMenuItem
            v-if="user.isTenantAdmin.value && company?.canChangeDetails"
            :data-testid="testIds.company.actions.edit"
            @click="modals.showEditCompany(company)"
          >
            <IconEdit class="size-4" />
            Edit Company
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </DataListItem>
  </DataList>
</template>
